/**
 * Browser Notification Service
 * Handles push notifications for task reminders
 */

export class NotificationService {
  constructor() {
    // iOS Safari tabs have no Notification global; a bare reference throws and blanks the app.
    this.permission = typeof Notification !== 'undefined' ? Notification.permission : 'default';
  }

  /**
   * Check if notifications are supported
   */
  isSupported() {
    return 'Notification' in window;
  }

  /**
   * Check if notifications are enabled
   */
  isEnabled() {
    return this.isSupported() && Notification.permission === 'granted';
  }

  /**
   * Request notification permission
   */
  async requestPermission() {
    if (!this.isSupported()) {
      throw new Error('Notifications not supported in this browser');
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      throw new Error('Notifications are blocked. Please enable them in browser settings.');
    }

    const permission = await Notification.requestPermission();
    this.permission = permission;
    return permission === 'granted';
  }

  /**
   * Show a notification
   * @param {string} title - Notification title
   * @param {object} options - Notification options
   */
  async showNotification(title, options = {}) {
    if (!this.isSupported()) {
      console.warn('Notifications not supported');
      return null;
    }

    if (!this.isEnabled()) {
      console.warn('Notifications not enabled');
      return null;
    }

    const defaultOptions = {
      icon: '/logo192.png',
      badge: '/logo192.png',
      vibrate: [200, 100, 200],
      requireInteraction: true, // Stays on screen until user dismisses
      tag: 'task-reminder', // Prevents duplicate notifications
      ...options
    };

    const notification = new Notification(title, defaultOptions);

    // Auto-close after 30 seconds if user doesn't interact
    setTimeout(() => {
      notification.close();
    }, 30000);

    return notification;
  }

  /**
   * Show task reminder notification
   * @param {object} task - Task object with title, time, etc.
   */
  async showTaskReminder(task) {
    const title = '⏰ Task Reminder';
    const body = `${task.title}\nDue at: ${task.time || 'No time set'}`;

    const notification = await this.showNotification(title, {
      body,
      icon: '/logo192.png',
      data: { taskId: task.id, type: 'task-reminder' }
    });

    if (notification) {
      // Handle notification click
      notification.onclick = () => {
        window.focus();
        notification.close();
        
        // Navigate to task (you can customize this)
        window.location.href = `/#/task/${task.id}`;
      };
    }

    return notification;
  }

  /**
   * Send test notification
   */
  async sendTestNotification() {
    return await this.showNotification('Test Notification', {
      body: 'Task reminders will appear like this!',
      icon: '/logo192.png'
    });
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
